import { useState } from 'react'
import { Badge, Button, Card, Input, LoadingBlock, cn, type BadgeTone } from '@quoter/web-core'
import { useI18n } from '@/hooks/useI18n'
import { useJourney, useSaveJourney } from '@/hooks/useJourney'
import { useChallengeMutations, useChallenges, type ChallengeView } from '@/hooks/useChallenges'
import {
  CHALLENGE_KEYS,
  PHASES,
  WASHOUT_DAYS,
  progress,
  suggestVerdict,
  withDay,
  type ChallengeKey,
  type ChallengeStatus,
  type Phase,
  type Score,
} from '@/lib/plan'
import { daysBetween, today } from '@/lib/dates'
import { ScorePicker } from '@/components/ScorePicker'

const STATUS_TONE: Record<ChallengeStatus | 'not_started', BadgeTone> = {
  not_started: 'neutral',
  in_progress: 'brand',
  tolerated: 'success',
  limited: 'warning',
  avoid: 'danger',
}

const VERDICTS = ['tolerated', 'limited', 'avoid'] as const

/** The three phases, and the reintroduction challenges with their outcome. */
export function Plan() {
  const { t } = useI18n()
  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <header>
        <h1 className="font-display text-[34px] font-normal tracking-tightish text-gray-900">{t.plan.title}</h1>
        <p className="mt-1 text-[13.5px] text-gray-500">{t.plan.subtitle}</p>
      </header>
      <Phases />
      <Challenges />
      <p className="text-[12px] leading-relaxed text-gray-500">{t.disclaimer}</p>
    </div>
  )
}

function Phases() {
  const { t, fmt } = useI18n()
  const { journey, loading } = useJourney()
  const save = useSaveJourney()

  if (loading) return <LoadingBlock />

  function moveTo(phase: Phase) {
    save.mutate({
      phase,
      // Entering elimination without a start date: it starts today.
      ...(phase === 'elimination' && !journey.eliminationStartedOn ? { eliminationStartedOn: today() } : {}),
    })
  }

  const eliminationDay = journey.eliminationStartedOn ? daysBetween(journey.eliminationStartedOn, today()) + 1 : null

  return (
    <ol className="space-y-3">
      {PHASES.map((phase) => {
        const current = journey.phase === phase
        const copy = t.plan.phases[phase]
        return (
          <li key={phase}>
            <Card tone={current ? 'sage' : 'surface'} className={cn(!current && 'shadow-none ring-1 ring-gray-200')}>
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <h2 className="font-display text-[20px] font-normal text-gray-900">{copy.name}</h2>
                <span className="text-[12.5px] font-semibold text-gray-500">{copy.length}</span>
              </div>
              <p className="mt-2 text-[14px] leading-relaxed text-gray-700">{copy.body}</p>

              {current && phase === 'elimination' && (
                <div className="mt-4 flex flex-wrap items-end gap-x-4 gap-y-2">
                  <label className="flex flex-col text-[12.5px] font-semibold text-gray-600">
                    {t.plan.startedOn}
                    <Input
                      type="date"
                      max={today()}
                      value={journey.eliminationStartedOn ?? ''}
                      onChange={(e) => save.mutate({ eliminationStartedOn: e.target.value || null })}
                      className="mt-1.5 w-44 bg-surface"
                    />
                  </label>
                  {eliminationDay !== null && eliminationDay > 0 && (
                    <span className="pb-2.5 text-[13.5px] font-semibold text-sage-800">
                      {fmt(t.plan.dayN, { n: eliminationDay })}
                    </span>
                  )}
                </div>
              )}
              {current && phase === 'elimination' && eliminationDay !== null && eliminationDay >= 14 && (
                <p className="mt-3 text-[13px] text-sage-800">{t.plan.readyHint}</p>
              )}

              <div className="mt-4">
                {current ? (
                  <Badge tone="success">{t.plan.youAreHere}</Badge>
                ) : (
                  <Button variant="secondary" size="sm" onClick={() => moveTo(phase)} disabled={save.isPending}>
                    {t.plan.moveHere}
                  </Button>
                )}
              </div>
            </Card>
          </li>
        )
      })}
      {save.isError && (
        <p className="text-[12.5px] text-red-700" role="alert">
          {t.common.error}
        </p>
      )}
    </ol>
  )
}

function Challenges() {
  const { t, fmt } = useI18n()
  const { challenges, loading, error } = useChallenges()
  const [open, setOpen] = useState<ChallengeKey | null>(null)

  const active = CHALLENGE_KEYS.find((k) => challenges[k]?.status === 'in_progress') ?? null
  const { done, total } = progress(Object.values(challenges).map((c) => c.status))
  // The one being tested opens by itself; the person can still open another.
  const shown = open ?? active

  return (
    <section>
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 className="font-display text-[24px] font-normal text-gray-900">{t.plan.challengesTitle}</h2>
        <span className="text-[13px] font-semibold text-gray-500">{fmt(t.plan.progress, { done, total })}</span>
      </div>
      <p className="mt-1.5 max-w-2xl text-[13.5px] leading-relaxed text-gray-600">{t.plan.challengesHint}</p>

      <div
        className="mt-4 h-2 overflow-hidden rounded-full bg-gray-200"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={done}
        aria-label={fmt(t.plan.progress, { done, total })}
      >
        <div className="h-full rounded-full bg-sage-600 transition-all" style={{ width: `${(done / total) * 100}%` }} />
      </div>

      {loading ? (
        <div className="mt-5">
          <LoadingBlock />
        </div>
      ) : error ? (
        <p className="mt-5 text-[13px] text-red-700" role="alert">
          {t.common.error}
        </p>
      ) : (
        <ul className="mt-5 space-y-2.5">
          {CHALLENGE_KEYS.map((key) => (
            <ChallengeRow
              key={key}
              challengeKey={key}
              challenge={challenges[key] ?? null}
              activeKey={active}
              open={shown === key}
              onToggle={() => setOpen(shown === key ? null : key)}
            />
          ))}
        </ul>
      )}
    </section>
  )
}

/**
 * Starting the first challenge is what moving into reintroduction means, so
 * the phase follows by itself — nobody should have to tell the app twice.
 */
function useStartChallenge() {
  const { start } = useChallengeMutations()
  const { journey } = useJourney()
  const saveJourney = useSaveJourney()
  return {
    start: (key: ChallengeKey) =>
      start.mutate(key, {
        onSuccess: () => {
          if (journey.phase === 'elimination') saveJourney.mutate({ phase: 'reintroduction' })
        },
      }),
    isPending: start.isPending,
    isError: start.isError,
  }
}

function ChallengeRow({
  challengeKey,
  challenge,
  activeKey,
  open,
  onToggle,
}: {
  challengeKey: ChallengeKey
  challenge: ChallengeView | null
  /** The challenge being tested right now, if any — only one at a time. */
  activeKey: ChallengeKey | null
  open: boolean
  onToggle: () => void
}) {
  const { t, fmt } = useI18n()
  const { update, reset } = useChallengeMutations()
  const start = useStartChallenge()
  const copy = t.plan.challenges[challengeKey]
  const status = challenge?.status ?? 'not_started'
  const busy = start.isPending || update.isPending || reset.isPending
  const blockedBy = !challenge && activeKey && activeKey !== challengeKey ? activeKey : null
  const suggestion = challenge ? suggestVerdict(challenge.days) : null
  const portions = [copy.day1, copy.day2, copy.day3]

  function setDay(day: number, score: Score, note: string) {
    if (!challenge) return
    update.mutate({ id: challenge.id, days: withDay(challenge.days, { day, score, note }) })
  }

  return (
    <li className="overflow-hidden rounded-lg bg-surface shadow-card">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-gray-900/[0.03]"
      >
        <span className="min-w-0 flex-1">
          <span className="block font-display text-[17px] text-gray-900">{copy.name}</span>
          <span className="mt-0.5 block truncate text-[12.5px] text-gray-500">{copy.found}</span>
        </span>
        <Badge tone={STATUS_TONE[status]}>{t.plan.status[status]}</Badge>
      </button>

      {open && (
        <div className="space-y-5 border-t border-DEFAULT px-5 py-5">
          <p className="text-[13.5px] text-gray-700">
            <span className="font-semibold text-gray-900">{t.plan.testFood}:</span> {copy.food}
          </p>

          {!challenge ? (
            <>
              <ol className="grid gap-2 sm:grid-cols-3">
                {portions.map((portion, i) => (
                  <li key={i} className="rounded-md bg-surface-muted px-4 py-3">
                    <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500">
                      {fmt(t.plan.day, { n: i + 1 })}
                    </p>
                    <p className="mt-1 text-[14px] font-semibold text-gray-900">{portion}</p>
                  </li>
                ))}
              </ol>
              {blockedBy ? (
                <p className="text-[13px] text-gray-500">
                  {fmt(t.plan.oneAtATime, { name: t.plan.challenges[blockedBy].name })}
                </p>
              ) : (
                <Button onClick={() => start.start(challengeKey)} disabled={busy}>
                  {t.plan.start}
                </Button>
              )}
            </>
          ) : (
            <>
              <ol className="space-y-3">
                {portions.map((portion, i) => {
                  const day = i + 1
                  const logged = challenge.days.find((d) => d.day === day) ?? null
                  return (
                    <li key={day} className="rounded-md bg-surface-muted px-4 py-3.5">
                      <div className="flex flex-wrap items-baseline justify-between gap-x-4">
                        <p className="text-[14px] font-semibold text-gray-900">
                          {fmt(t.plan.day, { n: day })} · {portion}
                        </p>
                        {!logged && <span className="text-[12px] text-gray-500">{t.plan.notDoneYet}</span>}
                      </div>
                      <p className="mb-2 mt-2.5 text-[12.5px] font-semibold text-gray-600">{t.plan.howWasIt}</p>
                      <ScorePicker
                        size="sm"
                        value={logged?.score ?? null}
                        onChange={(score) => setDay(day, score, logged?.note ?? '')}
                        disabled={busy}
                      />
                      {logged && (
                        <DayNote
                          key={`${challenge.id}-${day}-${logged.note}`}
                          initial={logged.note}
                          placeholder={t.plan.dayNotePlaceholder}
                          onSave={(note) => setDay(day, logged.score, note)}
                        />
                      )}
                    </li>
                  )
                })}
              </ol>
              <p className="text-[12.5px] leading-relaxed text-gray-500">{t.plan.stopEarly}</p>

              <div>
                <p className="text-[12.5px] font-semibold text-gray-600">{t.plan.verdictTitle}</p>
                <div className="mt-2 grid gap-2 sm:grid-cols-3">
                  {VERDICTS.map((verdict) => {
                    const chosen = challenge.status === verdict
                    return (
                      <button
                        key={verdict}
                        type="button"
                        onClick={() => update.mutate({ id: challenge.id, status: verdict })}
                        disabled={busy || challenge.days.length === 0}
                        aria-pressed={chosen}
                        className={cn(
                          'rounded-md border px-4 py-3 text-left transition-colors disabled:opacity-50',
                          chosen ? 'border-ink bg-ink text-gray-50' : 'border-DEFAULT hover:bg-gray-900/[0.05]',
                        )}
                      >
                        <span className="block text-[14px] font-semibold">
                          {t.plan.status[verdict]}
                          {!chosen && challenge.status === 'in_progress' && suggestion === verdict && (
                            <span className="ml-1.5 text-[11px] font-bold uppercase tracking-wide text-brand-700">
                              {t.plan.suggested}
                            </span>
                          )}
                        </span>
                        <span className={cn('mt-0.5 block text-[12px]', chosen ? 'text-gray-300' : 'text-gray-500')}>
                          {t.plan.verdictHelp[verdict]}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>

              {challenge.status !== 'in_progress' && (
                <p className="rounded-md bg-sage-50 px-4 py-3 text-[13px] text-sage-900">
                  {fmt(t.plan.restFirst, { n: WASHOUT_DAYS })}
                </p>
              )}

              <button
                type="button"
                onClick={() => {
                  if (window.confirm(t.plan.resetConfirm)) reset.mutate(challenge.id)
                }}
                disabled={busy}
                className="text-[12.5px] font-semibold text-gray-500 underline underline-offset-2 hover:text-red-700"
              >
                {t.plan.reset}
              </button>
            </>
          )}

          {(start.isError || update.isError || reset.isError) && (
            <p className="text-[12.5px] text-red-700" role="alert">
              {t.common.error}
            </p>
          )}
        </div>
      )}
    </li>
  )
}

/** One line of free text per test day, saved when the field loses focus. */
function DayNote({ initial, placeholder, onSave }: { initial: string; placeholder: string; onSave: (note: string) => void }) {
  const [value, setValue] = useState(initial)
  return (
    <Input
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => {
        const next = value.trim()
        if (next !== initial) onSave(next)
      }}
      placeholder={placeholder}
      aria-label={placeholder}
      maxLength={200}
      className="mt-3 bg-surface"
    />
  )
}
